import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { ChartComponentProps, getBaseOption, formatTooltipValue, ECHARTS_OPTS, CHART_GRID_LEFT } from './chartConfig';

export const CandlestickChart: React.FC<ChartComponentProps> = ({ chart, height = 320, colors }) => {
  const option = useMemo((): EChartsOption => {
    const data = chart.data || [];
    if (data.length === 0) return {};

    const base = getBaseOption(colors);

    const categories = data.map(d => String(d.name ?? d.date ?? ''));
    const ohlc = data.map(d => [
      Number(d.open) || 0,
      Number(d.close) || 0,
      Number(d.low) || 0,
      Number(d.high) || 0,
    ]);

    return {
      ...base,
      grid: { top: 40, right: 20, bottom: 44, left: CHART_GRID_LEFT, containLabel: true },
      xAxis: {
        type: 'category',
        data: categories,
        axisLabel: { fontSize: 11, color: '#7B6B72' },
        axisTick: { alignWithLabel: true },
      },
      yAxis: {
        type: 'value',
        axisLabel: { fontSize: 11, color: '#7B6B72' },
        splitLine: { lineStyle: { color: '#EDE5D8', type: 'dashed' } },
      },
      tooltip: {
        ...(base.tooltip as object),
        trigger: 'axis',
        axisPointer: { type: 'cross' as const },
        formatter: (params: unknown) => {
          const p = (Array.isArray(params) ? params[0] : params) as { name?: string; value?: number[] };
          const [open, close, low, high] = p.value ?? [];
          return `<div style="font-size:12px"><b>${p.name}</b><br/>Open: ${formatTooltipValue(open)}<br/>Close: ${formatTooltipValue(close)}<br/>Low: ${formatTooltipValue(low)}<br/>High: ${formatTooltipValue(high)}</div>`;
        },
      },
      series: [
        {
          type: 'candlestick',
          data: ohlc,
          itemStyle: { color: '#3F7D4F', color0: '#B23A48', borderColor: '#3F7D4F', borderColor0: '#B23A48' },
        },
      ],
      legend: { show: false },
    };
  }, [chart, colors]);

  return <ReactECharts option={option} style={{ height }} notMerge lazyUpdate opts={ECHARTS_OPTS} />;
};
